// ANGEL FLIGHT — MenuScreen (DOM). Title, the fantasy, DEPLOY + ARMORY.
//
// FLIR-styled DOM port of the old Phaser MenuScene: the "ANGEL FLIGHT" title +
// tagline and a uniform mission list (equal rows; LOCKED/biome/CLEARED subs) +
// ARMORY. Navigation is delegated to injected callbacks so the screen stays
// decoupled from App.
//
// GATING (shared credit economy): per the owner spec, Mission 1 (campaign idx 0)
// + the ENDLESS Last Stand mode are FREE; Missions 2/3/4 are CREDIT UNLOCKS
// (progression.ts isMissionUnlocked). A locked row renders an UNLOCK WALL — the
// mission title + price + the player's live balance + the earn-or-unlock message
// + an UNLOCK button (Platform.unlock). The list rebuilds in place after a
// purchase so the freshly-owned row + new balance show immediately. While
// `Platform.flags.unlockAll` is on nothing is gated and the wall never appears
// (the SDK default is now GATED — unlockAll=false — so the initial paint locks
// correctly even before /platform-config.json loads). The list also subscribes
// to Platform.onChange so a late config load or a signed-in owner's entitlements
// (which arrive AFTER mount) re-gate the rows in place.

import type { Screen } from '../Router';
import type { SaveStateV1 } from '../../core/types';
import { MISSIONS, endless } from '../../missions/index';
import { Platform } from '../../platform';
import {
  chapterForIndex,
  isMissionUnlocked,
  unlockMission,
  type Chapter,
} from '../../progression';
import {
  FLIR,
  FONT,
  makeButton,
  makeScreenRoot,
  makeSubtitle,
  makeTitle,
  el,
  type ButtonHandle,
} from './ui';

export interface MenuScreenOptions {
  /** Live save (read for missionProgress + currencies). */
  save: SaveStateV1;
  /** Start the given mission. */
  onDeploy: (missionId: string) => void;
  /** Open the Armory / Hangar. */
  onArmory: () => void;
  /** Replay the "LAST BIRD UP" intro (shown only once it's been seen). */
  onReplayIntro?: () => void;
}

// A wider content band so the menu fills the frame instead of a thin centre
// strip (owner: "use the width"); ARMORY + bank share the axis.
const COL = 'min(520px, 88vw)';

/** A short, single-line sub style shared by mission/mode rows. */
function subStyle(): Partial<CSSStyleDeclaration> {
  return {
    font: `500 clamp(10px, 1.8vw, 12px)/1.2 ${FONT}`,
    letterSpacing: '0.1em',
    color: FLIR.greenDim,
    textAlign: 'center',
    minHeight: '1.2em', // uniform row height even if a sub is short
    whiteSpace: 'nowrap',
  };
}

export class MenuScreen implements Screen<void> {
  private readonly opts: MenuScreenOptions;
  private root: HTMLDivElement | null = null;
  /** The mission-list container — rebuilt in place after a credit unlock. */
  private list: HTMLDivElement | null = null;
  private readonly buttons: ButtonHandle[] = [];
  /** Buttons that belong to the rebuildable mission list (disposed on rebuild). */
  private readonly listButtons: ButtonHandle[] = [];
  /** Unsubscribe for the Platform.onChange wallet/entitlement/config listener. */
  private unsub: (() => void) | null = null;

  constructor(opts: MenuScreenOptions) {
    this.opts = opts;
  }

  mount(parent: HTMLElement): void {
    const { save } = this.opts;

    const root = makeScreenRoot();
    root.style.gap = '12px';

    const title = makeTitle('ANGEL FLIGHT');
    title.style.marginTop = 'clamp(8px, 5vh, 56px)';
    const tagline = makeSubtitle('Behind the gun. Hold the line.');

    // SELECT MISSION header.
    const header = el(
      'div',
      {
        font: `700 clamp(13px, 2.4vw, 18px)/1 ${FONT}`,
        letterSpacing: '0.1em',
        color: FLIR.accent,
        marginTop: 'clamp(10px, 4vh, 34px)',
      },
      'SELECT MISSION',
    );

    // Mission list — each row deployable; Missions 2-4 are CREDIT UNLOCKS
    // (progression.isMissionUnlocked). The column is EXACTLY as wide as the
    // default button (and the buttons fill it), so every mission row shares one
    // centred axis with the ARMORY button below.
    const list = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '10px',
      marginTop: '12px',
      width: COL,
    });
    this.list = list;
    this.buildMissionRows();

    // ── GAME MODES: the endless "Last Stand" mode, distinct from the campaign.
    //    NEVER gated (it's a mode, not a mission); its sub shows the persisted
    //    best so the score chase is visible from the menu. ───────────────────
    const modesHeader = el(
      'div',
      {
        font: `700 clamp(13px, 2.4vw, 18px)/1 ${FONT}`,
        letterSpacing: '0.1em',
        color: FLIR.accent,
        marginTop: 'clamp(12px, 4vh, 30px)',
      },
      'GAME MODES',
    );
    const endlessBest = save.bestScores[endless.id] ?? 0;
    const endlessBtn = makeButton(endless.name.toUpperCase(), () => this.opts.onDeploy(endless.id), {
      width: COL,
    });
    this.buttons.push(endlessBtn);
    const endlessSub = el(
      'div',
      { ...subStyle(), marginTop: '2px' },
      endlessBest > 0 ? `ENDLESS HORDE · BEST ${endlessBest}` : 'ENDLESS HORDE · HOW LONG CAN YOU LAST?',
    );
    const endlessRow = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '2px',
      width: COL,
    });
    endlessRow.append(endlessBtn.el, endlessSub);

    const armory = makeButton('ARMORY', () => this.opts.onArmory(), { width: COL });
    armory.el.style.marginTop = '14px';
    this.buttons.push(armory);

    // The bank under the ARMORY door: the wallet is persistent and the menu
    // says so (mirrors the mission rows' one-line sub pattern). This is the
    // in-game SCRIP/SALVAGE bank — distinct from the shared CREDITS economy.
    const bankSub = el(
      'div',
      { ...subStyle(), marginTop: '2px' },
      `BANK ${save.scrip} SCRIP · ${save.salvage} SALVAGE`,
    );

    root.append(title, tagline, header, list, modesHeader, endlessRow, armory.el, bankSub);

    // Replay the campaign-opener intro — only offered once it's been seen
    // (a fresh save gets it on the first deploy anyway).
    const onReplay = this.opts.onReplayIntro;
    if (onReplay !== undefined && save.introSeen) {
      const replay = makeButton('REPLAY INTRO', () => onReplay());
      replay.el.style.marginTop = '4px';
      replay.el.style.opacity = '0.75';
      this.buttons.push(replay);
      root.append(replay.el);
    }
    parent.appendChild(root);
    this.root = root;

    // The wallet / entitlements / config can change AFTER mount — a late
    // /platform-config.json load, a signed-in owner's entitlements arriving, or a
    // credit spend in another same-origin game. Re-gate the mission rows in place
    // so locked/unlocked + live balances stay correct (mirrors zombie/echoloop).
    this.unsub = Platform.onChange(() => this.buildMissionRows());
  }

  /** (Re)build the mission rows into `this.list` — called on mount and again
   *  after a credit unlock so the newly-owned row + balance repaint in place. */
  private buildMissionRows(): void {
    const list = this.list;
    if (!list) return;
    const { save } = this.opts;
    for (const b of this.listButtons) b.destroy();
    this.listButtons.length = 0;
    list.replaceChildren();

    // Every mission renders the SAME uniform row: one full-width button + one
    // short sub. A LOCKED mission (a credit gate) renders an UNLOCK WALL instead.
    MISSIONS.forEach((m, i) => {
      const unlocked = isMissionUnlocked(i);
      const cleared = i < save.missionProgress;
      const best = save.bestScores[m.id] ?? 0;

      if (!unlocked) {
        list.append(this.makeLockedRow(m.name.toUpperCase(), i));
        return;
      }

      const sub = cleared
        ? `CLEARED${best > 0 ? ` · BEST ${best}` : ''}`
        : m.biome.toUpperCase();
      const btn = makeButton(m.name.toUpperCase(), () => this.opts.onDeploy(m.id), {
        accent: i === save.missionProgress,
        width: '100%', // fill the list column → same axis + width as ARMORY
      });
      const row = el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
      const subEl = el('div', subStyle(), sub);
      row.append(btn.el, subEl);
      list.append(row);
      this.listButtons.push(btn);
    });
  }

  /** The in-game UNLOCK WALL for a locked campaign mission: its title, the price,
   *  the player's live CREDITS balance, an "earn by playing, or unlock now"
   *  message, and an UNLOCK action (Platform.unlock via progression.unlockMission).
   *  On 'insufficient' it stays locked + shows the shortfall; signed-out players
   *  (balance 0, no earning) get a sign-in nudge. Re-renders the whole list on a
   *  successful purchase so the unlocked row + new balance show. */
  private makeLockedRow(missionName: string, idx: number): HTMLDivElement {
    const ch: Chapter | null = chapterForIndex(idx);
    const row = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      boxSizing: 'border-box',
      width: '100%',
      padding: '12px 16px',
      borderRadius: '10px',
      border: `1px solid ${FLIR.greenFaint}`,
      background: 'rgba(8,14,11,0.55)',
    });

    // Defensive: a locked row with no chapter should never occur, but never throw.
    if (ch === null) {
      row.append(el('div', { ...subStyle(), color: FLIR.warn }, `${missionName} · LOCKED`));
      return row;
    }

    const balance = Platform.balance();
    const shortfall = Math.max(0, ch.price - balance);

    // Title line: locked mission name + price.
    const titleLine = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
    });
    const name = el(
      'div',
      {
        font: `600 clamp(14px, 2.4vw, 17px)/1.1 ${FONT}`,
        letterSpacing: '0.05em',
        color: FLIR.green,
      },
      `LOCKED · ${ch.title}`,
    );
    const price = el(
      'div',
      {
        font: `700 clamp(13px, 2.2vw, 16px)/1 ${FONT}`,
        letterSpacing: '0.06em',
        color: FLIR.accent,
        whiteSpace: 'nowrap',
      },
      `${ch.price} CR`,
    );
    titleLine.append(name, price);

    // Balance + earn-or-unlock guidance.
    const balanceLine = el(
      'div',
      { ...subStyle(), textAlign: 'left', whiteSpace: 'normal' },
      `Your credits: ${balance} · Earn by playing, or unlock now.`,
    );

    const isAnon = balance === 0;
    const hint = el(
      'div',
      {
        ...subStyle(),
        textAlign: 'left',
        whiteSpace: 'normal',
        color: isAnon ? FLIR.accent : FLIR.greenDim,
      },
      isAnon
        ? 'Sign in (top-right) to earn credits and keep your unlocks.'
        : shortfall > 0
          ? `Need ${shortfall} more — clear missions or keep playing to earn credits.`
          : 'You can afford this — unlock it now.',
    );

    // UNLOCK action.
    const unlockBtn = makeButton(
      `UNLOCK · ${ch.price} CR`,
      () => {
        const result = unlockMission(ch);
        if (result === 'purchased' || result === 'owned') {
          // Rebuild the whole list so the now-owned row deploys + the balance
          // on any OTHER locked row repaints with the post-spend credits.
          this.buildMissionRows();
          return;
        }
        // 'insufficient' — keep it locked, show the live shortfall.
        const need = Math.max(0, ch.price - Platform.balance());
        hint.textContent =
          Platform.balance() === 0
            ? 'Sign in (top-right) to earn credits and keep your unlocks.'
            : `Not enough credits — need ${need} more. Keep playing to earn them.`;
        hint.style.color = FLIR.warn;
      },
      { width: '100%' },
    );
    // Slim the wall's CTA so a locked row stays close in height to a normal one.
    unlockBtn.el.style.minHeight = '46px';
    this.listButtons.push(unlockBtn);

    row.append(titleLine, balanceLine, hint, unlockBtn.el);
    return row;
  }

  unmount(): void {
    this.unsub?.();
    this.unsub = null;
    for (const b of this.listButtons) b.destroy();
    this.listButtons.length = 0;
    for (const b of this.buttons) b.destroy();
    this.buttons.length = 0;
    this.list = null;
    this.root?.remove();
    this.root = null;
  }
}
